import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { eventTypes, messageStatuses } from '../core/lib/enums';
import { CreateConnection } from '../core/vendors/signalr';
import { HubData, Notification, Process, SocketsConnection } from '../shared/interfaces';

@Injectable({
	providedIn: 'root',
})
export class LinqSocketsService {
	public notifications: Notification[] = [];
	public progress: { [key: string]: Process } = {};
	public connectionSubject$ = new BehaviorSubject<SocketsConnection>(null);

	private connection: SocketsConnection;

	/**
	 * Initialize a connection to the sockets server
	 * @param {string} url - The URL of the sockets service to which to
	 *   connect
	 * @param {string} hubName - The name of the sockets hub to which to
	 *   connect
	 * @param {string} userId - The ID of the current user
	 * @param {object} config - Configuration to pass to CreateConnection
	 */
	startConnection(url: string, hubName: string, userId: string, config?: object): void {
		if (this.connection) {
			return;
		}

		this.connection = CreateConnection(url, hubName, userId, config);

		// handle notifications
		this.connection.subscribe(eventTypes.notification, this.pushNotification.bind(this));

		// handle progress updates
		this.connection.subscribe(eventTypes.progress, this.handleProcess.bind(this));

		// publish to subject for external listeners
		this.connectionSubject$.next(this.connection);

		// connect and wait for updates
		this.connection.start();
	}

	/**
	 * End the current sockets connection
	 */
	stopConnection(): void {
		if (this.connection) {
			this.connection.stop();
		}

		// clear stored info
		this.notifications.length = 0;

		for (const id in this.progress) {
			if (Object.prototype.hasOwnProperty.call(this.progress, id)) {
				delete this.progress[id];
			}
		}

		// delete connection
		delete this.connection;

		// publish to subject for external listeners
		this.connectionSubject$.next(null);
	}

	// message utils
	/**
	 * Push a notification onto the service's notifications object
	 * @param {...HubData} notifications - The notification(s) to push
	 */
	pushNotification(...args: HubData[]): void {
		const now = Date.now();

		for (const element of args) {
			this.notifications.push({
				receivedAt: now,
				...element,
			});
		}
	}

	/**
	 * Remove a notification from the service's notifications object
	 * @param {Notification} dismissed - The notification to dismiss
	 */
	dismissNotification(dismissed: Notification): void {
		const index = this.notifications.indexOf(dismissed);
		this.notifications.splice(index, 1);
	}

	/** Remove all notifications from the service's notifications object */
	dismissAllNotifications(): void {
		this.notifications.length = 0;
	}

	/**
	 * Remove a process from the service's progress object
	 * @param {string} id - The message ID of the process to remove
	 */
	removeProcess(id: string): void {
		if (!this.progress[id]) {
			throw new Error(`Process with id '${id}' not found.`);
		}

		delete this.progress[id];
	}

	// internal util
	/**
	 * Add a process to the service's progress object
	 * @param message The raw message received from the sockets connection
	 */
	private handleProcess(message: any): void {
		const process: Process = {
			updatedAt: Date.now(),
			...message,
		};

		const oldProcess = this.progress[message.Id];

		// Don't process messages that are received out of order.  Stop processing messages once success or error is received.
		if (
			oldProcess &&
			(oldProcess.Timestamp > message.Timestamp || oldProcess.Status === messageStatuses.success || oldProcess.Status === messageStatuses.error)
		) {
			return;
		}

		// update frac based on status
		switch (message.Status) {
			case messageStatuses.starting:
				process.frac = 0;
				break;
			case messageStatuses.info:
				// if percentage wasn't given, keep the last value or default to 0
				if (!message.Data || typeof message.Data.Percentage === 'undefined') {
					process.frac = (oldProcess && oldProcess.frac) || 0;
					break;
				}

				process.frac = message.Data.Percentage / 100;
				break;
			case messageStatuses.success:
				process.frac = 1;
				break;
			case messageStatuses.error:
				if (oldProcess) {
					process.frac = oldProcess.frac;
				}

				break;
		}

		this.progress[message.Id] = process;
	}
}
